import type { LocationQuery } from 'vue-router'
import { useConstants } from '~/utils/constants'

const { QUERY_PARAMS } = useConstants()
const SUPPLIER_QUERY_KEY = QUERY_PARAMS.suppliers
const REFERENCE_QUERY_KEY = QUERY_PARAMS.referenceTypes
const REFERENCE_TAGS = ['office', 'pharmacy'] as const

function toStringArray(value: unknown): string[] {
  if (!value) return []
  return (Array.isArray(value) ? value : [value])
    .filter((entry): entry is string => typeof entry === 'string' && entry.length > 0)
}

export function sameSupplierHandles(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index])
}

export function supplierHandlesFromQuery(query: LocationQuery): string[] {
  return toStringArray(query[SUPPLIER_QUERY_KEY])
}

export function orderHistorySuppliersFromRoute(path: string, query: LocationQuery): string[] {
  if (!path.split('/').includes('order-history')) return []
  return supplierHandlesFromQuery(query)
}

export function referenceTagsFromQuery(query: LocationQuery): string[] {
  return toStringArray(query[REFERENCE_QUERY_KEY]).filter(tag =>
    (REFERENCE_TAGS as readonly string[]).includes(tag),
  )
}

export function orderHistoryReferencesFromRoute(path: string, query: LocationQuery): string[] {
  if (!path.split('/').includes('order-history')) return []
  return referenceTagsFromQuery(query)
}

function orderHistoryQuery(suppliers: readonly string[], references: readonly string[]): LocationQuery {
  const query: LocationQuery = {}
  if (suppliers.length > 0) query[SUPPLIER_QUERY_KEY] = [...suppliers]
  if (references.length > 0) query[REFERENCE_QUERY_KEY] = [...references]
  return query
}

/**
 * Campaign / last-order supplier filter in the URL (`?s=`).
 * Unknown query keys are ignored; writes rebuild the query from `s` only.
 */
export function useSupplierFilterQuery() {
  const route = useRoute()

  const supplierHandles = computed(() => supplierHandlesFromQuery(route.query))

  const referenceTags = computed(() => referenceTagsFromQuery(route.query))

  function setSupplierHandles(handles: readonly string[]): void {
    const next = [...new Set(toStringArray(handles))]
    if (sameSupplierHandles(supplierHandles.value, next)) return
    navigateTo({
      query: orderHistoryQuery(next, referenceTags.value),
    })
  }

  function setReferenceTags(tags: readonly string[]): void {
    const next = [...new Set(referenceTagsFromQuery({ [REFERENCE_QUERY_KEY]: [...tags] }))]
    if (sameSupplierHandles(referenceTags.value, next)) return
    navigateTo({
      query: orderHistoryQuery(supplierHandles.value, next),
    })
  }

  function setOrderHistoryFilters(handles: readonly string[], tags: readonly string[]): void {
    const nextSuppliers = [...new Set(toStringArray(handles))]
    const nextReferences = [...new Set(referenceTagsFromQuery({ [REFERENCE_QUERY_KEY]: [...tags] }))]
    const suppliersMatch = sameSupplierHandles(supplierHandles.value, nextSuppliers)
    const referencesMatch = sameSupplierHandles(referenceTags.value, nextReferences)
    if (suppliersMatch && referencesMatch) return
    navigateTo({
      query: orderHistoryQuery(nextSuppliers, nextReferences),
    })
  }

  return {
    supplierHandles,
    setSupplierHandles,
    referenceTags,
    setReferenceTags,
    setOrderHistoryFilters,
  }
}
